import { useState } from 'react';
import { Button } from '@mui/material';
import { PrinterIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { ThemeColors } from '../../context/ThemeContext';
import { inventoryApi } from '../../api/inventoryApi';
import { buildStockCountSheetHtml, type StockCountSession } from './stockCountSheet';

interface StockCountPrintSheetProps {
  theme: ThemeColors;
  /** Restaurant / vendor name printed in the sheet header. */
  vendorName?: string;
}

/**
 * "Print count sheet" button: reuses an existing IN_PROGRESS stock-count
 * session if one exists, otherwise starts a new one, then opens a printable
 * A4 worksheet in a new window.
 */
const StockCountPrintSheet = ({ theme, vendorName }: StockCountPrintSheetProps) => {
  const [loading, setLoading] = useState(false);

  const getOrCreateSession = async (): Promise<StockCountSession> => {
    const { data: history } = await inventoryApi.getStockCountHistory();
    const inProgress = (history as StockCountSession[] | undefined)?.find(
      (s) => s.status === 'IN_PROGRESS',
    );
    if (inProgress) {
      const { data } = await inventoryApi.getStockCount(inProgress.id);
      const session = data as StockCountSession;
      // A session snapshots items when started; one opened before any items
      // existed stays empty forever — start fresh instead of reusing it.
      if (session.items?.length) {
        return session;
      }
    }
    const { data } = await inventoryApi.createStockCount();
    return data as StockCountSession;
  };

  const handlePrint = async () => {
    if (loading) return;
    setLoading(true);
    // Open the window synchronously so the click gesture isn't lost to popup blockers.
    const printWindow = window.open('', '_blank');
    try {
      const session = await getOrCreateSession();
      const html = buildStockCountSheetHtml(session, vendorName);
      if (!printWindow) {
        toast.error('Allow pop-ups to print the count sheet.');
        return;
      }
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch {
      printWindow?.close();
      toast.error('Could not prepare the stock count sheet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      size="small"
      data-testid="stock-count-print-btn"
      startIcon={<PrinterIcon className="h-4 w-4" />}
      onClick={handlePrint}
      disabled={loading}
      sx={{
        backgroundColor: `${theme.button?.secondary?.bg || theme.background} !important`,
        color: `${theme.primary} !important`,
        border: `1px solid ${theme.primary} !important`,
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'none',
        boxShadow: 'none !important',
        '&:hover': {
          backgroundColor: `${theme.button?.secondary?.hover || theme.primary} !important`,
          boxShadow: 'none !important',
        },
      }}
    >
      {loading ? 'Preparing…' : 'Print count sheet'}
    </Button>
  );
};

export default StockCountPrintSheet;
